import { fetcher } from '@commenda/utils';

import {
  IIndirectTaxBreakdownExportParams,
  IIndirectTaxBreakdownJobResponse,
  IIndirectTaxFilingV2MutationResult,
  IndirectTaxFilingV2ExportMode,
} from './types';

/**
 * POST /api/v1/new-sales-tax/filings-v2/export/tax-breakdowns-advanced
 *
 * Full-surface breakdown CSV export for the admin indirect-tax tools
 * (MAIN_APP_ADMIN only). Unlike the three fixed export helpers, every upstream
 * filter and option is caller-supplied: multi-filing, transaction date range,
 * scenario set, invoice statuses, and line items. Leave deliveryRecipients
 * empty to skip the email and collect the CSV from the job instead.
 *
 * Async — returns a job id. Poll it with getSalesTaxJob, then turn the job's
 * export_file_id into a signed URL with getSalesTaxFile.
 */
export async function exportIndirectTaxBreakdowns({
  params,
  token,
}: {
  params: IIndirectTaxBreakdownExportParams;
  token?: string;
}): Promise<
  IIndirectTaxFilingV2MutationResult<IIndirectTaxBreakdownJobResponse>
> {
  try {
    // Caught here rather than upstream so the user sees which of the two
    // controls to change instead of a generic 400.
    if (
      params.includeLineItems &&
      params.exportMode === IndirectTaxFilingV2ExportMode.JURISDICTION_SPECIFIC
    ) {
      return {
        success: false,
        error:
          'Line items cannot be included in a jurisdiction-specific export. Switch the export mode to RAW, or turn line items off.',
      };
    }

    const body: Record<string, unknown> = {
      corporationId: params.corporationId,
      deliveryRecipients: params.deliveryRecipients ?? [],
      includeLineItems: params.includeLineItems ?? false,
    };

    if (params.filingId) body.filingId = params.filingId;
    if (params.filingIds?.length) body.filingIds = params.filingIds;
    if (params.filingDueDateStart) {
      body.filingDueDateStart = params.filingDueDateStart;
    }
    if (params.filingDueDateEnd) {
      body.filingDueDateEnd = params.filingDueDateEnd;
    }
    if (params.state) body.state = params.state;
    if (params.transactionStartDate) {
      body.transactionStartDate = params.transactionStartDate;
    }
    if (params.transactionEndDate) {
      body.transactionEndDate = params.transactionEndDate;
    }
    if (params.scenarios?.length) body.scenarios = params.scenarios;
    if (params.invoiceStatuses?.length) {
      body.invoiceStatuses = params.invoiceStatuses;
    }
    if (params.exportMode) body.exportMode = params.exportMode;

    const response = await fetcher(
      'new-sales-tax/filings-v2/export/tax-breakdowns-advanced',
      { method: 'POST', body },
      token
    );

    const json = await response.json();

    if (!response.ok) {
      const message = Array.isArray(json?.message)
        ? json.message.join(', ')
        : json?.message;
      return {
        success: false,
        error:
          typeof message === 'string' && message
            ? message
            : 'Failed to export tax breakdowns',
      };
    }

    return {
      success: true,
      data: { jobId: json?.jobId, message: json?.message ?? '' },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to export tax breakdowns',
    };
  }
}
